import {
  faArrowDownToLine,
  faSliders,
} from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useIntl } from 'react-intl'
import { styled } from 'styled-components'
import { useBoolean } from 'usehooks-ts'

import { ButtonSizes, FormattedMessage } from '@b-stock/fe-core/bstock-react'
import { Breakpoints, Colors, Typography } from '@b-stock/fe-core/design-system'

import { useDownloadManifest } from '@/buyer-portal/helpers/useDownloadManifest'

import { useManifestTableTracking } from '../../../hooks/useManifestTableTracking'
import { getVisibleStartEnd, useManifestContext } from '../ManifestContext'
import { ControlButton, ControlButtonLabel } from '../ui'
import { ColumnSelect } from './ColumnSelect'
import { ManifestFiltersBar } from './ManifestFiltersBar'
import { SearchInput } from './SearchInput'

const Bar = styled.div`
  background-color: ${Colors.Semantic.Neutral.light_grey};
  border-bottom: 0.0625rem solid ${Colors.Semantic.Neutral.medium_grey};
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  padding: 0.75rem 1rem;
`

const Left = styled.div`
  align-items: center;
  display: flex;
  flex: 1;
  gap: 0.75rem;
`

const RowCount = styled.span`
  ${Typography.Body4}
  color: ${Colors.Semantic.Neutral.dark_grey};
  white-space: nowrap;

  @media ${Breakpoints.max.medium} {
    display: none;
  }
`

const ButtonGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`

export const ManifestSearchBar = () => {
  const { formatMessage } = useIntl()
  const { filters, isLoading, limit, listing, total, visibleRange } =
    useManifestContext()
  const { end, start } = getVisibleStartEnd(visibleRange, limit, total)
  const downloadManifest = useDownloadManifest()
  const { onDownloadFullManifest, onManifestFiltersButtonClicked } =
    useManifestTableTracking(listing)
  const { toggle: toggleFilters, value: showFilters } = useBoolean(false)

  const activeFilterCount = Object.values(filters).reduce(
    (total, values) => total + values.length,
    0
  )

  return (
    <>
      <Bar>
        <Left>
          <SearchInput />
          {!isLoading && (
            <RowCount>
              <FormattedMessage
                id={`Listing.manifestAgGrid.searchBar.${total > 0 ? 'showing' : 'noResults'}`}
                values={{ end, start, total }}
              />
            </RowCount>
          )}
        </Left>
        <ButtonGroup>
          <ColumnSelect />
          <ControlButton
            appearance={showFilters ? 'primary' : 'secondary'}
            onClick={() => {
              onManifestFiltersButtonClicked()
              toggleFilters()
            }}
            size={ButtonSizes.small}
            title={formatMessage({
              id: 'Listing.manifestAgGrid.searchBar.filters',
            })}
          >
            <FontAwesomeIcon icon={faSliders} />
            <ControlButtonLabel>
              <FormattedMessage id="Listing.manifestAgGrid.searchBar.filters" />
              {activeFilterCount > 0 && ` (${activeFilterCount})`}
            </ControlButtonLabel>
          </ControlButton>
          <ControlButton
            appearance="secondary"
            onClick={() => {
              onDownloadFullManifest()
              void downloadManifest(listing.lot._id, listing.lot.title)
            }}
            size={ButtonSizes.small}
            title={formatMessage({
              id: 'Listing.manifestAgGrid.searchBar.downloadCsv',
            })}
          >
            <FontAwesomeIcon icon={faArrowDownToLine} />
            <ControlButtonLabel>
              <FormattedMessage id="Listing.manifestAgGrid.searchBar.downloadCsv" />
            </ControlButtonLabel>
          </ControlButton>
        </ButtonGroup>
      </Bar>
      {showFilters && <ManifestFiltersBar />}
    </>
  )
}
